import {
  AHQueryHandlerErrorFallbackStatic,
  AHQueryHandlerLoading,
  AHText,
  Box,
  ScaleFactorProvider,
  useScaleFactor,
} from "arkh";

import React, {
  Suspense,
  useCallback,
  useTransition,
} from "react";

import { useFragment, graphql } from "react-relay";

import { SignalFocusScreen_widgetSignalOverview$key } from "./__generated__/SignalFocusScreen_widgetSignalOverview.graphql";
import { SignalDetail } from "./SignalDetail";
import { ErrorBoundary } from "react-error-boundary";
import { StyleSheet } from "react-native";
import { WidgetSignalOverviewContentMedium } from "widgets/src/WidgetSignalOverview/WidgetSignalOverviewContentMedium";
import { SignalFocusScreenListHeader_widgetSignalOverview$key } from "./__generated__/SignalFocusScreenListHeader_widgetSignalOverview.graphql";

export const SignalFocusScreen = (props: {
  widget: SignalFocusScreen_widgetSignalOverview$key;
  signalId?: string;
  storecastId: string;
  focusItem: (id: string) => void;
  isTouchMode: boolean;
}) => {
  const widget = useFragment(
    graphql`
      fragment SignalFocusScreen_widgetSignalOverview on WidgetSignalOverview {
        id
        title
        ...SignalFocusScreenListHeader_widgetSignalOverview
        ...WidgetSignalOverviewContentMediumPaginated
      }
    `,
    props.widget
  );

  const sf = useScaleFactor();
  const [isPending, startTransition] = useTransition();
  const [delayedSignalId, setDelayedSignalId] = React.useState<
    string | undefined
  >(props.signalId);

  const { focusItem } = props;

  const onSelectDetailItem = useCallback(
    (id: string) => {
      startTransition(() => {
        setDelayedSignalId(id);
      });
      focusItem(id);
    },
    [focusItem]
  );

  const noOp = useCallback(() => { }, []);

  return (
    <Box
      space="xlarge"
      style={{
        flex: 1,
        width: "100%",
        alignSelf: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        style={{
          flex: 1,
          flexDirection: "row",
          overflow: "hidden",
          pointerEvents: "auto",
        }}
        cornerRadius="medium"
        color="surface"
      >
        <Box
          style={{
            flexBasis: "20%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Suspense fallback={null}>
            <ListHeader widget={widget} />
          </Suspense>
          <WidgetSignalOverviewContentMedium
            widget={widget}
            isFocused
            shouldDimInactive={false}
            onPressItem={onSelectDetailItem}
            setScroll={noOp}
            interactive
            highlightedItemId={delayedSignalId}
          />
        </Box>
        {props.signalId ? (
          <Box
            style={{
              flexBasis: "80%",
              borderLeftWidth: StyleSheet.hairlineWidth,
            }}
            borderColor="surfaceBorder"
          >
            <ScaleFactorProvider value={sf * 1.2}>
              <ErrorBoundary
                FallbackComponent={AHQueryHandlerErrorFallbackStatic}
              >
                <Suspense fallback={<AHQueryHandlerLoading />}>
                  <SignalDetail
                    signalId={props.signalId}
                    storecastId={props.storecastId}
                  />
                </Suspense>
              </ErrorBoundary>
            </ScaleFactorProvider>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

function ListHeader(props: {
  widget: SignalFocusScreenListHeader_widgetSignalOverview$key;
}) {
  const widget = useFragment(
    graphql`
      fragment SignalFocusScreenListHeader_widgetSignalOverview on WidgetSignalOverview {
        title
        teams {
          name
        }
      }
    `,
    props.widget
  );

  return (
    <Box space="medium">
      <AHText type="title">{widget.title}</AHText>
      <AHText type="detail">
        {widget.teams.map(({ name }) => name).join(", ")}
      </AHText>
    </Box>
  );
}
